/**
 * Utility functions for the application.
 */

/**
 * Format numbers with thousand separators
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Format dates
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format dates in short format (M/D/YY)
 */
export const formatShortDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

/**
 * Format datetime
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Debounce function for search inputs
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Download data as CSV
 */
export const downloadCSV = (data, filename) => {
  if (!data || data.length === 0) return;

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Get error message from API error
 */
export const getErrorMessage = (error) => {
  const correlationId =
    error?.correlationId ||
    error?.response?.headers?.['x-correlation-id'] ||
    error?.config?.headers?.['X-Correlation-ID'];

  const withCorrelationId = (message) => {
    if (!correlationId) return message;
    return `${message} (Correlation ID: ${correlationId})`;
  };

  if (error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.name === 'CanceledError') {
    return 'Request was canceled';
  }

  if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
    return withCorrelationId('Request timed out. Please try again.');
  }

  if (!error.response && (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || ''))) {
    return withCorrelationId('Network error reaching the API. Check VPN/proxy connectivity and retry.');
  }

  if (error.response?.status === 401) {
    return withCorrelationId('Authentication failed. Please sign out and sign back in.');
  }

  if (error.response?.status === 403) {
    return withCorrelationId('You do not have access to this data.');
  }

  if (error.response?.status === 503) {
    return withCorrelationId('Service temporarily unavailable. Please retry in a few minutes.');
  }

  if (error.response?.data?.detail) {
    return withCorrelationId(error.response.data.detail);
  }
  if (error.response?.data?.message) {
    return withCorrelationId(error.response.data.message);
  }
  if (error.message) {
    return withCorrelationId(error.message);
  }
  return withCorrelationId('An unexpected error occurred');
};

/**
 * Validate material review record
 */
export const validateMaterialRecord = (record) => {
  const errors = [];

  // Maximum Lot Size >= Minimum Lot Size
  if (record.MaximumLotSize !== null && record.MinimumLotSize !== null) {
    if (parseFloat(record.MaximumLotSize) < parseFloat(record.MinimumLotSize)) {
      errors.push('Maximum Lot Size must be >= Minimum Lot Size');
    }
  }

  // Maximum Stock Level >= Absolute Minimum Inventory
  if (record.MaximumStockLevel !== null && record.AbsoluteMinimum !== null) {
    if (parseFloat(record.MaximumStockLevel) < parseFloat(record.AbsoluteMinimum)) {
      errors.push('Maximum Stock Level must be >= Absolute Minimum Inventory');
    }
  }

  // Target Inventory >= Safety Stock
  if (record.TargetInventory !== null && record.SafetyStock !== null) {
    if (parseFloat(record.TargetInventory) < parseFloat(record.SafetyStock)) {
      errors.push('Target Inventory must be >= Safety Stock');
    }
  }

  // Target Inventory >= Absolute Minimum Inventory
  if (record.TargetInventory !== null && record.AbsoluteMinimum !== null) {
    if (parseFloat(record.TargetInventory) < parseFloat(record.AbsoluteMinimum)) {
      errors.push('Target Inventory must be >= Absolute Minimum Inventory');
    }
  }

  return errors;
};

/**
 * Group array by key
 */
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Deep clone object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};
